import moment from 'moment'
import 'moment-timezone'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { DateInputPropsType } from '../../utils/types'
import { useCallback, useMemo } from 'react'
import {
  DATE_INPUT_CONSTANTS,
  DATE_PICKER_CONSTANTS,
  DATE_TIME_PICKER_INPUT_DISPLAY_FORMATS,
  FIELD_VALUE_CONSTANTS,
} from '../../utils'
import {
  formatDatePickerChangeValue,
  formatDateTimePickerChangeAsUtcIso,
  getDatePickerTimeBounds,
  parseDatePickerSelectedValue,
  parseDateTimePickerValueInTz,
  resolveDatePickerMinMax,
} from '../../utils/datePickerHelpers'
import { DatePickerCalendarIcon } from './DatePickerCalendarIcon'
import {
  MANUAL_DATE_TIME_PICKER_INPUT_PROPS,
  useDatePickerOpenState,
  useManualDateTimeInputHandlers,
  useStableCalendarContainer,
} from './useDatePickerHooks'

const DateInput = ({
  isDisabled,
  value,
  handleChange,
  className,
  showClear,
  showIcon = true,
  tabIndex,
  name,
  min,
  max,
  placeholder,
  includeTime,
}: DateInputPropsType) => {
  const browserTimeZone = useMemo(() => moment.tz.guess(), [])
  const { minDate: minDateObj, maxDate: maxDateObj } = useMemo(
    () => resolveDatePickerMinMax(min, max),
    [min, max]
  )

  const {
    isOpen,
    setIsOpen,
    uniqueString,
    closeCalendar,
    openCalendar,
    toggleCalendar,
    handleClickOutside,
  } = useDatePickerOpenState(name)

  const selectedValueFormat = includeTime
    ? DATE_INPUT_CONSTANTS.COMMON_DATE_TIME_FORMAT_2
    : DATE_INPUT_CONSTANTS.COMMON_DATE_FORMAT

  const selected = useMemo(
    () => parseDatePickerSelectedValue(value, selectedValueFormat),
    [value, selectedValueFormat]
  )

  const handeOnChange = useCallback(
    (date: Date | null) => {
      if (handleChange) {
        if (date) {
          handleChange(formatDatePickerChangeValue(date, includeTime))
        } else {
          handleChange('')
        }
      }
      if (!includeTime) {
        setIsOpen(false)
      }
    },
    [handleChange, includeTime, setIsOpen]
  )

  const calendarContainer = useStableCalendarContainer(
    showClear,
    handeOnChange,
    closeCalendar
  )

  const { minTime, maxTime } = useMemo(
    () => getDatePickerTimeBounds(value, minDateObj, maxDateObj),
    [value, minDateObj, maxDateObj]
  )

  const { handleChangeRaw, handleInputBlur } = useManualDateTimeInputHandlers(
    handleChange,
    isDisabled,
    browserTimeZone
  )

  const pickerInputClassName = `${className} ${DATE_PICKER_CONSTANTS.PICKER_INPUT_CLASS_SUFFIX}`

  return (
    <DatePicker
      name={name}
      selected={selected}
      open={isOpen}
      closeOnScroll={false}
      onClickOutside={handleClickOutside}
      onSelect={() => {
        if (!includeTime) {
          setIsOpen(false)
        }
      }}
      onInputClick={() => {
        if (!isDisabled && !includeTime) {
          openCalendar()
        }
      }}
      className={pickerInputClassName}
      onChange={handeOnChange}
      onChangeRaw={includeTime ? handleChangeRaw : undefined}
      onBlur={includeTime ? handleInputBlur : undefined}
      disabled={isDisabled}
      dateFormat={
        includeTime
          ? DATE_TIME_PICKER_INPUT_DISPLAY_FORMATS
          : DATE_INPUT_CONSTANTS.COMMON_DATE_FORMAT_FOR_DATE_INPUT
      }
      placeholderText={placeholder}
      icon={
        <DatePickerCalendarIcon
          uniqueString={uniqueString}
          isDisabled={isDisabled}
          onToggle={toggleCalendar}
        />
      }
      showIcon={showIcon}
      wrapperClassName={DATE_PICKER_CONSTANTS.WRAPPER_CLASSNAME}
      calendarContainer={calendarContainer}
      showYearDropdown
      dropdownMode={DATE_PICKER_CONSTANTS.DROPDOWN_MODE}
      yearDropdownItemNumber={DATE_INPUT_CONSTANTS.DEFAULT_YEARS_TO_SHOW}
      popperClassName={DATE_PICKER_CONSTANTS.POPPER_CLASSNAME}
      popperPlacement={DATE_PICKER_CONSTANTS.POPPER_PLACEMENT}
      tabIndex={tabIndex}
      minDate={minDateObj}
      maxDate={maxDateObj}
      shouldCloseOnSelect={!includeTime}
      {...(includeTime
        ? {
            ...MANUAL_DATE_TIME_PICKER_INPUT_PROPS,
            showTimeSelect: true,
            timeFormat: DATE_PICKER_CONSTANTS.TIME_FORMAT,
            timeIntervals: FIELD_VALUE_CONSTANTS.FIFTEEN,
            timeCaption: DATE_PICKER_CONSTANTS.TIME_CAPTION,
            minTime,
            maxTime,
          }
        : {})}
    />
  )
}

export default DateInput

export const DateTimeInputComponent = ({
  isDisabled,
  value,
  handleChange,
  className,
  showClear,
  showIcon = true,
  tabIndex,
  name,
  min,
  max,
  placeholder,
}: DateInputPropsType) => {
  const browserTimeZone = useMemo(() => moment.tz.guess(), [])

  const {
    isOpen,
    uniqueString,
    closeCalendar,
    toggleCalendar,
    handleClickOutside,
  } = useDatePickerOpenState(name)

  const { handleChangeRaw, handleInputBlur } = useManualDateTimeInputHandlers(
    handleChange,
    isDisabled,
    browserTimeZone
  )

  const dateInTz = useMemo(
    () => parseDateTimePickerValueInTz(value, browserTimeZone),
    [value, browserTimeZone]
  )

  const handleOnChange = useCallback(
    (date: Date | null) => {
      if (!handleChange) return

      if (!date) {
        handleChange('')
      } else {
        handleChange(formatDateTimePickerChangeAsUtcIso(date, browserTimeZone))
      }
    },
    [handleChange, browserTimeZone]
  )

  const calendarContainer = useStableCalendarContainer(
    showClear,
    handleOnChange,
    closeCalendar
  )

  const { minDate, maxDate } = useMemo(
    () => resolveDatePickerMinMax(min, max),
    [min, max]
  )

  const pickerInputClassName = `${className} ${DATE_PICKER_CONSTANTS.PICKER_INPUT_CLASS_SUFFIX}`

  return (
    <DatePicker
      name={name}
      selected={dateInTz}
      open={isOpen}
      closeOnScroll={false}
      onClickOutside={handleClickOutside}
      className={pickerInputClassName}
      onChange={handleOnChange}
      onChangeRaw={handleChangeRaw}
      onBlur={handleInputBlur}
      {...MANUAL_DATE_TIME_PICKER_INPUT_PROPS}
      showTimeSelect
      timeFormat={DATE_PICKER_CONSTANTS.TIME_FORMAT}
      timeIntervals={FIELD_VALUE_CONSTANTS.FIFTEEN}
      timeCaption={DATE_PICKER_CONSTANTS.TIME_CAPTION}
      disabled={isDisabled}
      dateFormat={DATE_TIME_PICKER_INPUT_DISPLAY_FORMATS}
      placeholderText={placeholder}
      icon={
        <DatePickerCalendarIcon
          uniqueString={uniqueString}
          isDisabled={isDisabled}
          onToggle={toggleCalendar}
        />
      }
      showIcon={showIcon}
      wrapperClassName={DATE_PICKER_CONSTANTS.WRAPPER_CLASSNAME}
      calendarContainer={calendarContainer}
      showYearDropdown
      dropdownMode={DATE_PICKER_CONSTANTS.DROPDOWN_MODE}
      yearDropdownItemNumber={DATE_INPUT_CONSTANTS.DEFAULT_YEARS_TO_SHOW}
      popperClassName={DATE_PICKER_CONSTANTS.POPPER_CLASSNAME}
      popperPlacement={DATE_PICKER_CONSTANTS.POPPER_PLACEMENT}
      tabIndex={tabIndex}
      minDate={minDate}
      maxDate={maxDate}
      shouldCloseOnSelect={false}
    />
  )
}
